import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Target,
  Trophy,
  Handshake,
  Megaphone,
  Building2,
  Smartphone,
  ChevronRight,
  Check,
} from "lucide-react";

const services = [
  {
    id: "strategic",
    icon: Target,
    title: "Strategic Consulting",
    subtitle: "Data-Driven Business Strategy",
    description:
      "We help organizations navigate complex challenges with comprehensive strategic planning. Our consultants analyze your market position, identify opportunities, and develop actionable roadmaps for sustainable growth.",
    features: [
      "Market analysis and competitive assessment",
      "Business model optimization",
      "Growth strategy development",
      "Operational excellence consulting",
      "Change management support",
      "Performance metrics and KPI frameworks",
    ],
    image:
      "https://customer-assets.emergentagent.com/job_anglin-consult/artifacts/jo70aepq_strategic%20consulting.png",
  },
  {
    id: "sports",
    icon: Trophy,
    title: "Sports & Event Program Development",
    subtitle: "World-Class Event Execution",
    description:
      "From conceptualization to execution, we deliver exceptional sports programs and events. Our expertise covers league development, tournament management, and creating unforgettable experiences for athletes and spectators alike.",
    features: [
      "League and tournament development",
      "Event planning and management",
      "Athlete development programs",
      "Youth sports initiatives",
      "Facility planning and operations",
      "Safety and risk management",
    ],
    image:
      "https://customer-assets.emergentagent.com/job_anglin-consult/artifacts/fd72t8nc_sports%20and%20event%20program%20development.png",
  },
  {
    id: "sponsorship",
    icon: Handshake,
    title: "Sponsorship Proposals",
    subtitle: "Partnerships That Deliver ROI",
    description:
      "We create compelling sponsorship packages that connect brands with the right opportunities. Our proven approach helps secure partnerships that deliver measurable value for all stakeholders.",
    features: [
      "Sponsorship strategy development",
      "Proposal creation and presentation",
      "Sponsor identification and outreach",
      "Partnership negotiation",
      "Activation planning",
      "ROI measurement and reporting",
    ],
    image:
      "https://customer-assets.emergentagent.com/job_anglin-consult/artifacts/8mbjzwis_sponsorship%20proposals.png",
  },
  {
    id: "brand",
    icon: Megaphone,
    title: "Brand Positioning & Marketing",
    subtitle: "Build Recognition and Trust",
    description:
      "Strategic brand development that resonates in competitive markets. We help organizations build strong identities, connect with target audiences, and create lasting impressions that drive business results.",
    features: [
      "Brand strategy and positioning",
      "Visual identity development",
      "Marketing campaign planning",
      "Digital marketing strategy",
      "Content strategy and creation",
      "Public relations support",
    ],
    image:
      "https://customer-assets.emergentagent.com/job_anglin-consult/artifacts/l4bc0rx7_brand%20position%20marketing.png",
  },
  {
    id: "governance",
    icon: Building2,
    title: "Governance & Structure Development",
    subtitle: "Build Sustainable Organizations",
    description:
      "We help sports bodies and businesses establish robust governance frameworks that ensure accountability, compliance, and long-term sustainability. Our expertise covers policy development, board advisory, and organizational design.",
    features: [
      "Governance framework design",
      "Policy and procedure development",
      "Board training and advisory",
      "Compliance assessment",
      "Organizational restructuring",
      "Succession planning",
    ],
    image:
      "https://customer-assets.emergentagent.com/job_anglin-consult/artifacts/yyfvs15h_governance.png",
  },
  {
    id: "app",
    icon: Smartphone,
    title: "App Development",
    subtitle: "Custom Digital Solutions",
    description:
      "We build mobile and web applications tailored to your business needs. From concept to deployment, our development team creates digital solutions that enhance engagement, streamline operations, and drive growth.",
    features: [
      "Mobile app development (iOS & Android)",
      "Web application development",
      "Custom software solutions",
      "UI/UX design",
      "API development and integration",
      "Ongoing maintenance and support",
    ],
    image:
      "https://images.unsplash.com/photo-1551650975-87deedd944c3?w=600&h=400&fit=crop",
  },
];

const ServicesPage = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.slice(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
      }
    }
  }, [location]);

  return (
    <main data-testid="services-page" className="pt-20">
      {/* Hero Section */}
      <section className="section-padding bg-[#020617] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0F172A] via-[#020617] to-[#172554] pointer-events-none" />
        <div className="absolute inset-0 noise-overlay pointer-events-none" />

        <div className="section-container relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <span className="label-accent mb-4 block">What We Offer</span>
            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold uppercase tracking-tight text-white leading-[0.9] mb-6">
              Our
              <br />
              <span className="text-[#E53935]">Services</span>
            </h1>
            <p className="text-slate-400 text-lg leading-relaxed">
              Comprehensive consulting solutions designed to elevate your business 
              and sports endeavors. We combine strategic expertise with hands-on 
              execution to deliver measurable results.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services List */}
      <section className="bg-[#0F172A]">
        {services.map((service, index) => (
          <div
            key={service.id}
            id={service.id}
            data-testid={`service-detail-${service.id}`}
            className={`section-padding border-b border-[#1E293B] ${
              index % 2 === 0 ? "bg-[#0F172A]" : "bg-[#020617]"
            }`}
          >
            <div className="section-container">
              <div
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center ${
                  index % 2 === 1 ? "lg:flex-row-reverse" : ""
                }`}
              >
                {/* Content */}
                <motion.div
                  initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                  viewport={{ once: true }}
                  className={index % 2 === 1 ? "lg:order-2" : ""}
                >
                  {/* Icon & Title */}
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-14 h-14 rounded-sm bg-[#E53935]/10 flex items-center justify-center">
                      <service.icon className="w-7 h-7 text-[#E53935]" />
                    </div>
                    <span className="label-accent">{service.subtitle}</span>
                  </div>

                  <h2 className="font-heading text-4xl md:text-5xl font-bold uppercase tracking-tight text-white mb-6">
                    {service.title}
                  </h2>

                  <p className="text-slate-400 leading-relaxed mb-8 text-lg">
                    {service.description}
                  </p>

                  {/* Features */}
                  <ul className="space-y-3 mb-8">
                    {service.features.map((feature) => (
                      <li
                        key={feature}
                        className="flex items-start gap-3 text-slate-300"
                      >
                        <Check className="w-5 h-5 text-[#E53935] flex-shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Link
                    to="/contact"
                    className="btn-primary inline-flex items-center gap-2"
                  >
                    Get Started
                    <ChevronRight className="w-5 h-5" />
                  </Link>
                </motion.div>

                {/* Image */}
                <motion.div
                  initial={{ opacity: 0, x: index % 2 === 0 ? 30 : -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                  viewport={{ once: true }}
                  className={`relative ${index % 2 === 1 ? "lg:order-1" : ""}`}
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div
                    className={`absolute -bottom-4 ${
                      index % 2 === 0 ? "-right-4" : "-left-4"
                    } w-full h-full border-2 border-[#E53935] -z-10`}
                  />
                </motion.div>
              </div>
            </div>
          </div>
        ))}
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-[#020617] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#3B4B8C]/10 via-transparent to-[#E53935]/10 pointer-events-none" />

        <div className="section-container relative z-10 text-center">
          <span className="label-accent mb-4 block">Ready to Start?</span>
          <h2 className="font-heading text-4xl md:text-5xl font-bold uppercase tracking-tight text-white mb-6">
            Let's Discuss Your
            <br />
            <span className="text-[#E53935]">Project</span>
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto mb-10 text-lg">
            Schedule a consultation to explore how our services can help you 
            achieve your goals.
          </p>
          <Link
            to="/contact"
            data-testid="services-cta-btn"
            className="btn-primary inline-flex items-center gap-2"
          >
            Book a Consultation
            <ChevronRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </main>
  );
};

export default ServicesPage;
